import axios, { type AxiosInstance, type AxiosError } from "axios"
import * as Sentry from "@sentry/react"
import { API_BASE_URL } from "@/shared/lib/constants"
import { useErrorToastStore } from "@/shared/stores/errorToastStore"
import { extractErrorMessage, getErrorStatusCode } from "@/shared/lib/errorMessages"

// Extend axios config to support error toast metadata
declare module 'axios' {
  export interface AxiosRequestConfig {
    metadata?: {
      skipGlobalErrorToast?: boolean
      handledByAxiosInterceptor?: boolean
    }
  }
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
  withCredentials: true, // Send cookies with requests
})

// Request interceptor - no longer needed for cookie-based auth
// Cookies are sent automatically by the browser
apiClient.interceptors.request.use(
  (config) => {
    // Debug logging for URLs
    const fullUrl = `${config.baseURL}${config.url}`
    console.log('[API Request]', {
      method: config.method?.toUpperCase(),
      url: config.url,
      fullUrl,
      urlLength: config.url?.length
    })

    // Check for invalid characters in URL
    if (config.url && config.url.includes('undefined')) {
      console.error('[API Request] URL contains undefined!', config.url)
    }

    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor - handle errors globally
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Check if component wants to handle error locally
    const skipGlobalToast = error.config?.metadata?.skipGlobalErrorToast;

    // Only show toast for 4xx and 5xx errors
    const statusCode = getErrorStatusCode(error);
    const shouldShowToast = !skipGlobalToast && statusCode && statusCode >= 400;

    if (shouldShowToast) {
      const message = extractErrorMessage(error);
      const { addToast } = useErrorToastStore.getState();

      // Add error toast
      addToast(statusCode, message);

      // Log errors in development mode
      if (import.meta.env.DEV) {
        console.error('[API Error]', {
          statusCode,
          message,
          url: error.config?.url,
          method: error.config?.method,
          error,
        });
      }

      // Capture 5xx errors to Sentry (server errors worth tracking)
      if (statusCode && statusCode >= 500) {
        Sentry.captureException(error, {
          tags: {
            status_code: String(statusCode),
            api_url: error.config?.url || 'unknown',
            api_method: error.config?.method?.toUpperCase() || 'unknown',
          },
          extra: {
            response_data: error.response?.data,
            request_url: error.config?.url,
          },
        });
      }

      // Special handling for 401: Show toast + redirect to login after 2 seconds
      if (statusCode === 401) {
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      }

      // Mark as handled by axios interceptor
      if (error.config) {
        error.config.metadata = {
          ...error.config.metadata,
          handledByAxiosInterceptor: true,
        };
      }
    }

    return Promise.reject(error);
  }
)
